/**
 * WebSocket connection context for Aegix One.
 *
 * Provides a single multiplexed connection to `api/v2/ws/` that is
 * established on login and torn down on logout. Consumers subscribe to
 * raw socket messages via `useSocketMessage`; domain-specific routing
 * lives in `SocketMessageRouter`.
 *
 * On every successful (re)connect the client fetches the unified
 * recovery snapshot (`GET /api/v2/alerts/snapshot/`) and reconciles it
 * into the React Query cache — an authoritative single round-trip that
 * also removes state that terminated while the client was offline,
 * rather than a bulk invalidate. A generation counter guards against a
 * double-fetch if the socket briefly flaps within a single reconnect
 * attempt.
 *
 * @see apps/aegix-one-react/src/lib/socket/socket.ts
 * @see apps/aegix-one-react/src/services/snapshot.ts
 * @see doc/adr/decisions/078-realtime-resilience-architecture.md — no client-side polling
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useIsAuthenticated,
  useUser,
  type AllauthUser,
} from '@aegix/auth-identity';
import {
  useSocketService,
  type MessageListener,
} from '../lib/socket/socket';
import type { ConnectionState } from '../lib/socket/types';
import { fetchSnapshot } from '../services/snapshot';
import { applySnapshot } from './socket-handlers/applySnapshot';

interface SocketContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  isReconnecting: boolean;
  addMessageListener: (listener: MessageListener) => void;
  removeMessageListener: (listener: MessageListener) => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

export function SocketProvider({ children }: PropsWithChildren) {
  const isAuthenticated = useIsAuthenticated();
  const user = useUser<AllauthUser>();
  const queryClient = useQueryClient();

  // Per-connect generation counter. Increments on every `onConnect` so
  // that a late-arriving snapshot response from a stale generation can
  // no-op against the current cache.
  const connectGenerationRef = useRef(0);

  const handleConnect = useCallback(() => {
    const generation = ++connectGenerationRef.current;
    fetchSnapshot()
      .then((snapshot) => {
        if (generation !== connectGenerationRef.current) return;
        applySnapshot(snapshot, {
          queryClient,
          // `@aegix/auth-identity`'s `BaseUser.id` is typed
          // as `number | string` to accommodate adapters whose
          // backends use string IDs. ONE's Django backend always
          // issues integer PKs (AllauthUser.id), so coerce at the
          // handler boundary which expects `number | null`.
          currentUserId:
            typeof user?.id === 'number' ? user.id : null,
        });
      })
      .catch((error) => {
        console.error(
          '[SocketContext] Snapshot recovery failed; falling back to broad invalidation:',
          error,
        );
        // Best-effort fallback: bulk-invalidate the reunification keys so the
        // UI still converges against the server, just with N round trips
        // instead of 1.
        queryClient.invalidateQueries({ queryKey: ['events'] });
        queryClient.invalidateQueries({ queryKey: ['event'] });
        queryClient.invalidateQueries({ queryKey: ['event-students'] });
        queryClient.invalidateQueries({ queryKey: ['guardian-self'] });
        queryClient.invalidateQueries({ queryKey: ['my-students'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['unread-count'] });
      });
  }, [queryClient, user?.id]);

  const { addMessageListener, removeMessageListener, connectionState } =
    useSocketService(isAuthenticated, {
      onConnect: handleConnect,
    });

  const value = useMemo<SocketContextValue>(
    () => ({
      connectionState,
      isConnected: connectionState.status === 'connected',
      isReconnecting: connectionState.status === 'reconnecting',
      addMessageListener,
      removeMessageListener,
    }),
    [connectionState, addMessageListener, removeMessageListener],
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}

/**
 * Subscribe to raw socket messages. Automatically registers on mount and
 * unregisters on unmount, so handler de-registration is handled for callers.
 */
export function useSocketMessage(handler: MessageListener) {
  const { addMessageListener, removeMessageListener } = useSocket();
  useEffect(() => {
    addMessageListener(handler);
    return () => removeMessageListener(handler);
  }, [handler, addMessageListener, removeMessageListener]);
}

export default SocketContext;
